<template>
  <div :class="`officials-results${!showingOfficials ? '--empty' : ''}`">
    <div
      v-if="!showingOfficials"
      class="w-100 d-flex justify-content-center">
      <div
        v-if="loadingOfficials"
        class="spinner-border"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
      <div
        v-else
        class="officials-results__no-results-found">
        {{ $t('cac.app.no_results_found') }}
      </div>
    </div>
    <accordion-row
      v-for="race of getFilteredOfficialsByRaceAndParty()"
      :key="race.key"
      :id="`officials-results--${race.key}`"
      :label="$t(`cac.app.${race.key}`)"
    >
      <div class="officials-results__grid row">
        <div
          v-for="party of race.parties"
          :key="party.key"
          :id="`officials-results--${race.key}-${party.key}`"
          :class="`col officials-results__column officials-results__column--${party.key}`"
        >
          <official-profile-card-compact
            v-for="official of party.officials"
            :key="official.id"
            :id="`officials-results--${race.key}-${party.key}-${official.id}`"
            :official="official"
          />
        </div>
        <!-- Need a column at the end to ensure all columns are the same width on a given screen size -->
        <div class="col officials-results__column officials-results__column--dummy" />
      </div>
    </accordion-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import STATE from "Constants/campaign/civicactioncenter/state";
import AdvocateActions from "Constants/campaign/civicactioncenter/advocateActions";
import AccordionRow from "CivicActionCenterComponents/shared/OfficialsResults/AccordionRow.vue";
import OfficialProfileCardCompact from "CivicActionCenterComponents/shared/OfficialProfileCardCompact.vue";

import CANDIDATE_TYPES from "Constants/campaign/civicactioncenter/candidateTypes";
import TITLES from "Constants/campaign/civicactioncenter/officialTitles";
import PARTIES from "Constants/campaign/civicactioncenter/parties";
import RACES from "Constants/campaign/civicactioncenter/races";
import STAKEHOLDER_TYPES from "Constants/campaign/civicactioncenter/stakeholderTypes";

const allCandidates = `${STATE.SCOPE_OFFICIALS}/${
  STATE.CANDIDATE_PREVIEWS_ALL
}`;
const allElectedOfficials = `${STATE.SCOPE_OFFICIALS}/${
  STATE.ELECTED_OFFICIAL_PREVIEWS_ALL
}`;
const RACE_PRESIDENT = 'the-president';
const TYPE_MAJOR = 'major';

export default {
  name: "OfficialsResults",
  components: {
    AccordionRow,
    OfficialProfileCardCompact
  },
  inject: {
    apiHelper: { from: "apiHelper" }
  },
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    stakeholderType: {
      type: String,
      required: true,
      validator(val) {
        return [
          STAKEHOLDER_TYPES.CANDIDATE,
          STAKEHOLDER_TYPES.ELECTED_OFFICIAL
        ].includes(val);
      }
    }
  },
  data() {
    return {
      loadingOfficials: true
    };
  },
  computed: {
    ...mapGetters({
      advocateRecord: "forms/advocateRecord",
      allCandidates,
      allElectedOfficials
    }),
    ...mapState(STATE.SCOPE_FORMS, {
      honeypotPageLoadTime: state => state.honeypotPageLoadTime
    }),
    showingOfficials() {
      return this.getFilteredOfficialsByRaceAndParty().length > 0;
    },
    isCandidates() {
      return this.stakeholderType === STAKEHOLDER_TYPES.CANDIDATE;
    },
    allOfficials() {
      if (this.isCandidates) {
        return this.allCandidates;
      }

      return this.allElectedOfficials;
    },
    /**
     * Organizes unfiltered candidate data for use in the template.
     */
    allOfficialsByRaceAndParty() {
      return RACES.ALL_RACES_IN_ORDER.map(raceKey => ({
        key: raceKey,
        parties: PARTIES.ALL_PARTIES_IN_ORDER.map(partyKey => ({
          key: partyKey,
          officials: this.allOfficials
            .filter(official => {
              const raceMatches = official.race === raceKey;
              let partyMatches = official.party === partyKey;

              if (partyKey === PARTIES.OTHER) {
                partyMatches = this.isOtherParty(official.party);
              }
              
              const isMajor = official.race === RACE_PRESIDENT ?
                official.type === TYPE_MAJOR :
                true;

              return raceMatches && partyMatches && isMajor;
            })
            .sort(this.sortOfficials)
        }))
      }));
    }
  },
  watch: {
    advocateRecord() {
      this.getOfficials();
    }
  },
  created() {
    this.getOfficials();
  },
  methods: {
    async getOfficials() {
      const address = {
        address1: this.advocateRecord.address1,
        zip5: this.advocateRecord.zip5,
        zipcode: this.honeypotPageLoadTime
      };

      if (this.isCandidates) {
        await this.apiHelper.searchCandidates(address);
      } else {
        await this.apiHelper.searchLegislators(address);
      }

      this.loadingOfficials = false;

      await this.apiHelper.trackAdvocateAction(
        this.advocateRecord,
        AdvocateActions.MEET_THE_CANDIDATES
      );
    },
    sortOfficials(officialA, officialB) {
      if (
        officialA.title === TITLES.PRESIDENT &&
        officialB.title === TITLES.VICE_PRESIDENT
      )
        return -1;
      if (
        officialA.title === TITLES.VICE_PRESIDENT &&
        officialB.title === TITLES.PRESIDENT
      )
        return 1;

      if (officialA.incumbent && !officialB.incumbent) return -1;
      if (!officialA.incumbent && officialB.incumbent) return 1;

      // set single-name candidates to have an empty last name (instead of null)
      officialA.lastName = officialA.lastName || "";
      officialB.lastName = officialB.lastName || "";

      return officialA.lastName.localeCompare(officialB.lastName);
    },
    isOtherParty(party = "") {
      return (
        party === PARTIES.OTHER || !PARTIES.ALL_PARTIES_IN_ORDER.includes(party)
      );
    },
    /**
     * Takes structured data, filters out candidates based on filters,
     * and removes any races/parties with no candidates. Updates variable
     * that tracks whether any officials exist for the given filter.
     *
     * This has the same structure/shape as allOfficialsByRaceAndParty,
     * just with filtered and empty items removed.
     */
    getFilteredOfficialsByRaceAndParty() {
      if (this.allOfficials.length === 0) return [];

      const { ANY_TYPE, CHALLENGER, INCUMBENT } = CANDIDATE_TYPES;
      const { ANY_PARTY, OTHER } = PARTIES;

      const filteredType = this.filters.candidateType || ANY_TYPE;
      const filteredParty = this.filters.party || ANY_PARTY;

      const shouldShowOfficial = official => {
        if (filteredType === INCUMBENT && !official.incumbent) return false;
        if (filteredType === CHALLENGER && official.incumbent) return false;

        if (filteredParty !== ANY_PARTY && official.party !== filteredParty) {
          return filteredParty === OTHER && this.isOtherParty(official.party);
        }

        return true;
      };

      // Take the race/party data we already have, pull out any
      // filtered-out officials, then pull out any race/party combos
      // and/or overall races without any visible candidates.
      const finalFilteredOfficials = this.allOfficialsByRaceAndParty
        .map(race => ({
          key: race.key,
          parties: race.parties
            .map(party => ({
              key: party.key,
              officials: party.officials.filter(shouldShowOfficial)
            }))
            .filter(party => party.officials.length > 0)
        }))
        .filter(race => race.parties.length > 0);
      this.loadingOfficials = false;
      this.triggerWindowResize();
      return finalFilteredOfficials;
    },
    triggerWindowResize() {
      this.$nextTick(() => {
        // Trigger window resize since we have potentially large height changes here.
        // Need this for fixed-sidebar + responsive widget to function correctly
        const event = document.createEvent("Event");
        event.initEvent("resize", false, false);

        window.dispatchEvent(event);
      });
    }
  }
};
</script>
